<template>
  <div v-if="!multi" class="file-upload">
    <div
      v-if="!files"
      class="drop-area pa-4"
      @drop.prevent="handleUpload"
      @dragover.prevent
      @click="$refs.upload.click()"
    >
      <input ref="upload" type="file" @change="handleUpload" v-show="false" :accept="`.${allowedTypes.join(', .')}`" />
      <div class="content-wrapper text-center">
        <div class="mb-12">{{ label }}</div>

        <v-btn text color="primary" :ripple="false">
          <v-img class="mr-2" style="width: 20px; height: 20px" src="@/assets/img/upload.svg"></v-img>
          <span>Загрузить</span>
        </v-btn>
      </div>
    </div>
    <slot v-else name="list" v-bind="{ files, label, handleDeleteFile }">
      <AttachmentItem :attachment="files" :label="label" @remove="handleDeleteFile"></AttachmentItem>
    </slot>
  </div>
  <div v-else>
    <div
      v-if="!files.length"
      class="drop-area"
      @drop.prevent="handleUpload"
      @dragover.prevent
      @click="$refs.upload.click()"
    >
      <input
        ref="upload"
        type="file"
        @change="handleUpload"
        v-show="false"
        multiple
        :accept="`.${allowedTypes.join(', .')}`"
      />

      <div class="content-wrapper text-center">
        <div class="mb-2">{{ label }}</div>
        <v-btn text :ripple="false" class="pa-6" @drag="handleUpload" style="background: rgb(242, 242, 245)">
          <v-img class="mr-2" style="width: 20px; height: 20px" src="@/assets/img/upload.svg"></v-img>
          <span>Загрузить</span>
        </v-btn>
      </div>
    </div>

    <slot v-else name="list" v-bind="{ files, label }">
      <v-row class="mt-2">
        <div class="mb-2 mx-auto">{{ label }}</div>

        <v-col v-for="file in files" :key="file.id" cols="12">
          <div class="d-flex align-center justify-center">
            {{ file.file_name }}
            <v-btn v-if="file.id" color="error" @click.stop="onAttachmentRemove(file.id)" icon text>
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </div>
          <div class="d-flex justify-center my-2">
            <div :class="['fiv-cla', 'fiv-size-lg', getIcon(`image/${file.name.split('.')[1]}`)]"></div>
          </div>
        </v-col>
      </v-row>
    </slot>
    <slot name="list-append" v-bind="{ label }"></slot>
  </div>
</template>

<script>
import { uploadFile as uploadFileToServer } from "@/api/upload";
import icons from "file-icon-vectors/dist/icons/classic/catalog.json";
import * as mimeDb from "mime-db";

export default {
  name: "FileUploader",
  components: {
    AttachmentItem: () => import("@/components/common/AttachmentItem"),
  },
  data: () => ({
    dragging: false,
    baseColor: "rgba(0, 0, 0, 0.54)",
    activeColor: "rgb(73, 113, 208)",
  }),
  props: {
    label: {
      type: String,
    },
    groupName: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    multi: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array | Object,
      default: () => [],
    },
    allowedTypes: {
      type: Array,
      default: () => ["doc", "docx", "rtf", "pdf", "jpeg", "jpg", "gif", "png", "bmp", "tif", "tiff"],
    },
    maxSize: {
      type: Number,
      default: 1024 * 1024 * 40, // megabytes
    },
  },

  methods: {
    async handleFile(file) {
      if (file.size === 0) {
        this.$store
          .dispatch("common/addAlert", {
            msg: "Файл является недействительным",
            type: "error",
          })
          .then();

        return null;
      }

      const fileNameArr = file.name.split(".");
      const fileType = fileNameArr[fileNameArr.length - 1].toLowerCase();

      if (!this.allowedTypes.includes(fileType)) {
        this.$store
          .dispatch("common/addAlert", {
            msg: `Расширения файлов, доступные для загрузки: *.${this.allowedTypes.join(", *.")}`,
            type: "error",
          })
          .then();

        return null;
      }

      if (file.size > this.maxSize) {
        this.$store
          .dispatch("common/addAlert", {
            msg: `Загружать можно только файлы размером не более ${(this.maxSize / 1024 / 1024).toFixed(2)} мегабайт.`,
            type: "error",
          })
          .then();

        return null;
      }

      const formData = new FormData();
      formData.append("inputFile", file, file.name);

      const fileResponse = await uploadFileToServer(formData);
      if (!fileResponse) {
        this.$store
          .dispatch("common/addAlert", {
            msg: "Не удалось загрузить файл",
            type: "error",
          })
          .then();

        return null;
      }
      this.$emit("attachment-file-add", fileResponse);
    },

    onAttachmentRemove(id) {
      this.$emit("attachment-file-remove", id);
    },

    async handleUpload(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!this.multi) {
        if (files.length > 1) {
          this.$store
            .dispatch("common/addAlert", {
              msg: "Возможна загрузка не более одного файла",
              type: "error",
            })
            .then();

          return;
        }

        await this.handleFile(files[0]);
      } else {
        this.dragging = false;

        const lastFileIdx = files.length - 1;
        for (let i = 0; i <= lastFileIdx; ++i) {
          await this.handleFile(files[i], i === lastFileIdx);
        }

        // this.$refs.upload.value = "";
      }
    },
    getIcon(mimeType) {
      const iconName = mimeDb[mimeType]?.extensions?.find((extension) => icons.includes(extension)) ?? "blank";
      return "fiv-icon-" + iconName;
    },
  },
};
</script>

<style lang="scss">
.file-upload {
  position: relative;
  height: 100%;
  font-size: 14px;
  line-height: 20px;
  .drop-area {
    cursor: pointer;
    background: #f5f5f6;
    height: 100%;
    box-sizing: border-box;
    border-radius: 3px;
    width: 100%;
    display: block;
  }

  .v-input__slot {
    display: block;
  }
}

.dropZone {
  position: relative;
  height: 200px;
  border: 1px solid #cccccc;

  &-info {
    color: rgba(0, 0, 0, 0.54);
    position: absolute;
    top: 50%;
    width: 100%;
    transform: translate(0, -50%);
    text-align: center;
  }

  &-over {
    background: rgba(73, 113, 208, 0.12);

    & .dropZone-info {
      color: rgb(73, 113, 208);
    }
  }

  &:hover {
    background: rgba(73, 113, 208, 0.12);

    & .dropZone-info {
      color: rgb(73, 113, 208);
    }
  }

  input {
    position: absolute;
    cursor: pointer;
    top: 0px;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
  }
}
</style>
